import React, { useState, useRef, useEffect } from "react";
import {
  useLocation,
  useOutletContext,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import ApiMap from "../../utils/ApiMap";
import AuthService from "../../utils/AuthService";
import axios from "axios";
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const LessonPage = () => {
  const location = useLocation();
  const { lessons, lessonName, contentId } = location.state || {};
  const chapterinfo = React.useRef(null);
  const [progress, setProgress] = useState(0); // Overall progress
  const [chapter, setChapter] = useState(null); // Store the fetched lesson content
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const [isLessonLoading, setIsLessonLoading] = useState(false);
  const [loadingNxtPg, setLoadingNxtPg] = React.useState(false);
  const [loadingPrevPg, setLoadingPrevPg] = React.useState(false);
  const { courseId, moduleId, lessonId, chapterId } = useParams();

  const lessonRefs = useRef([]);
  const {
    isSidebarHidden,
    setIsSidebarHidden,
    lessonProgress,
    setLessonProgress,
    sidebarWidth,
  } = useOutletContext();
  const navigate = useNavigate();
  const isQuizPage = location.pathname.includes("quizpage");
  const isAssignmentPage = location.pathname.includes("assignment");
  const handleToggleSidebar = () => setIsSidebarHidden((prev) => !prev);

  useEffect(() => {
    if (isQuizPage) return;
    setIsLessonLoading(true);
    const fetchData = async () => {
      try {
        const resp = await axios(
          `${ApiMap.CONTENT}/coursecontent/${courseId}/module/${moduleId}/chapter/${chapterId}/lesson/${contentId}`,
          AuthService.getAuthHeader()
        );
        chapterinfo.current = resp.data;
        console.log(resp.data);
        setChapter({
          Name: resp.data.Name,
          ModuleName: resp.data.ModuleName,
          Title: resp.data.Title,
          Value: resp.data.Value,
          ContentFormat: resp.data.ContentFormat,
          Time: resp.data.DurationMin,
          totalPages: resp.data.TotalPages,
          currPage: resp.data.CurPgNum,
          ContentType: resp.data.ContentType,
        });

        setIsContentLoaded(true);
      } catch (err) {
        setError("Failed to load lesson content. Please try again.");
      } finally {
        setIsLoading(false);
        setIsLessonLoading(false);
      }
    };

    if (AuthService.isLoggedIn() && contentId) {
      fetchData();
    }
  }, [contentId, moduleId]); // Runs when contentId changes

  const getChapter = async (pgNum) => {
    const resp = await axios(
      `${ApiMap.CONTENT}/coursecontent/${courseId}/module/${moduleId}/chapter/${chapterId}/lesson/${contentId}?pgnum=${pgNum}`,
      AuthService.getAuthHeader()
    );

    chapterinfo.current.CurPgNum = resp.data.CurPgNum;
    setChapter({
      Name: resp.data.Name,
      Title: resp.data.Title,
      Value: resp.data.Value,
      ContentFormat: resp.data.ContentFormat,
      ModuleName: resp.data.ModuleName,
      Time: resp.data.DurationMin,
      totalPages: resp.data.TotalPages,
      currPage: resp.data.CurPgNum,
      ContentType: resp.data.ContentType,
    });
  };

  useEffect(() => {
    if (lessonName && lessonRefs.current.length > 0 && chapter) {
      const targetIndex = chapter.Name === lessonName ? 0 : -1;
      if (targetIndex >= 0 && lessonRefs.current[targetIndex]) {
        lessonRefs.current[targetIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [lessonName, chapter]);

  useEffect(() => {
    if (!isContentLoaded) return; // Skip if content is not loaded
    if (progress === 100) {
      setLessonProgress((prevLessonProgress) => ({
        ...prevLessonProgress,
        [contentId]: 100, // Explicitly set progress to 100 for the current lesson
      }));
    }

    const handleScroll = () => {
      if (!lessonRefs.current[0]) return; // Ensure the lessonRef exists
      const updatedLessonProgress = { ...lessonProgress };
      // Get the reference to the single lesson
      const lessonRef = lessonRefs.current[0];
      const rect = lessonRef.getBoundingClientRect();
      const lessonHeight = lessonRef.offsetHeight;
      // Calculate the visible progress of the single lesson
      const scrollTop = Math.max(0, window.innerHeight - rect.top);
      const lessonProgressValue = Math.min(
        (scrollTop / lessonHeight) * 100,
        100
      );
      updatedLessonProgress[contentId] = Math.round(lessonProgressValue);

      // Calculate the overall progress
      const visiblePercentage = Math.min(
        Math.max(0, window.innerHeight - rect.top) / lessonHeight,
        1
      );
      const totalProgress = visiblePercentage * 100;

      // Update progress states
      setLessonProgress(updatedLessonProgress);
      setProgress(totalProgress);
    };

    // Attach scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener
    return () => window.removeEventListener("scroll", handleScroll);
  }, [contentId, isContentLoaded]);

  const handleNext = () => {
    console.log(chapterinfo);

    // Check if current page is less than total pages
    if (chapterinfo.current.CurPgNum < chapterinfo.current.TotalPages) {
      setLoadingNxtPg(true); // Set loading to true to show the spinner
      getChapter(parseInt(chapterinfo.current.CurPgNum) + 1)
        .then(() => {
          setLoadingNxtPg(false); // Set loading to false once the chapter is fetched
        })
        .catch(() => {
          setLoadingNxtPg(false); // In case of error, set loading to false
        });
    }
  };

  const handleBack = () => {
    // setActiveStep(chapterinfo.current.CurPgNum);
    setLoadingPrevPg(true);
    getChapter(chapterinfo.current.CurPgNum - 1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  const handleGoToQuiz = () => {
    navigate("quizpage", {
      state: { courseId, moduleId, chapterId, contentId },
    });
  };

  const handleAssignment = () => {
    navigate("assignment", {
      state: { courseId, moduleId, chapterId, contentId },
    });
  };

  if (!chapter) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography>No Lesson available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          padding: "16px",
        }}
      >
        <IconButton
          onClick={handleToggleSidebar}
          sx={{ position: "fixed", top: 0, left: sidebarWidth, zIndex: 1000 }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Progress Bar */}

      <Box sx={{ padding: "20px" }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            marginBottom: "20px",
            color: "primary.main",
          }}
        >
          {chapter.ModuleName || "Course Name"}
        </Typography>
      </Box>
      <Box
        sx={{
          position: "fixed",
          top: 0, // adjust according to your header height
          left: sidebarWidth, // to align with your layout
          right: 0,
          zIndex: 1200,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flex: 1, // ensures the progress bar takes remaining space
            }}
          />
        </Box>
      </Box>

      {/* Lesson Content */}

      {!isQuizPage &&
        !isAssignmentPage &&
        chapter &&
        (isLessonLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <LinearProgress
              color="secondary"
              sx={{
                width: "200px", // Controls the progress bar width
                height: "4px",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              padding: "25px",
              minWidth: "400px",
              maxWidth: "920px",
              margin: "0 auto",
            }}
          >
            <Box
              ref={(el) => (lessonRefs.current[0] = el)}
              sx={{
                padding: 2,
                borderRadius: 2,
                boxShadow: 1,
                marginBottom: 5,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  fontStyle: "italic",
                  color: "secondary.main",
                  padding: "2px 5px",
                }}
              >
                Lesson 1 of 1 <br />
                Duration: {chapter.Time} minutes
              </Typography>
              <Box
                sx={{
                  lineHeight: 1.5,
                }}
              >
                {chapter.Value !== "No Content yet" ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: chapter?.Value }}
                    sx={{
                      width: "100%",
                      fontSize: "0.9rem",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      maxWidth: "100%",
                      overflow: "visible", // no clipping or scrollbars
                      "& h1:first-of-type": {
                        textAlign: "center",
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                      },
                      "& table": {
                        width: "auto",
                        borderCollapse: "collapse",
                        tableLayout: "auto", // allows the table to fit naturally
                        maxWidth: "100%", // Ensures the table stays within bounds of container
                        overflowX: "auto", // Enables horizontal scroll if needed
                      },
                      "& td, & th": {
                        wordBreak: "break-word", // Ensures no overflow within table cells
                        padding: "8px", // Optional: Adds padding for readability
                        textOverflow: "ellipsis", // Handles overflow text if necessary
                        whiteSpace: "normal", // Prevents text from overflowing
                      },
                    }}
                  />
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {chapter.Name}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 400,
                        textAlign: "center",
                        marginBottom: "30px",
                        color: "text.secondary",
                      }}
                    >
                      {chapter.Title.replace(/<[^>]*>/g, "")}
                    </Typography>
                  </>
                )}
                {console.log(chapter)}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography variant="body2" sx={{ marginTop: "8px" }}>
                    Pages:
                    {` ${chapterinfo.current.CurPgNum} / ${chapter.totalPages}`}
                  </Typography>
                </Box>

                {chapter.totalPages > 1 && chapter.ContentType !== "writing" ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleBack}
                      disabled={chapterinfo.current.CurPgNum == 1}
                    >
                      ← Back
                    </Button>

                    <Button
                      variant="contained"
                      disabled={
                        chapterinfo.current.CurPgNum >=
                          chapterinfo.current.TotalPages || loadingNxtPg
                      }
                      onClick={handleNext}
                      startIcon={
                        loadingNxtPg ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {loadingNxtPg ? "Loading..." : "Next →"}
                    </Button>
                  </Box>
                ) : null}
              </Box>
              {chapter.ContentType === "formative_assessment" && (
                <Box sx={{ textAlign: "right", marginTop: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      padding: "5px 10px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                    }}
                    onClick={handleGoToQuiz}
                  >
                    Quiz
                    <ArrowForwardIcon
                      sx={{
                        marginLeft: "8px",
                      }}
                    />
                  </Button>
                </Box>
              )}
              {chapter.ContentType === "writing" && (
                <Box sx={{ textAlign: "right", marginTop: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      padding: "5px 10px",
                      borderRadius: "20px",
                      fontWeight: "bold",
                    }}
                    onClick={handleAssignment}
                  >
                    Enter
                    <ArrowForwardIcon
                      sx={{
                        marginLeft: "8px",
                      }}
                    />
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      {(isQuizPage || isAssignmentPage) && <Outlet />}
    </Box>
  );
};

export default LessonPage;
